import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useReducer, useState, } from 'react';
import { CHARGERS } from '../../data/chargers/CHARGERS.js';
import LIABILITY_RATES from '../../data/insurance/liability.json';
import COMPREHENSIVE_RATES from '../../data/insurance/comprehensive.json';
import { LCFS, EER_VALUES, gridIntensity as GRID_INTENSITY, } from '../../constants';
import { stateAccess } from './calculations.state.js';
import calculationsReducer from './calculations.reducer';
import { INITIAL_RESULTS_STATE } from './calculations.state.samples.js';
export * from './calculations.types';
const CalculationsContext = createContext({});
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function requireDynamically(path) {
    return eval(`import('${path}');`); // Ensure Webpack does not analyze the require statement
}
export const CalculationsProvider = ({ children, dynamicModulePath, chargers = CHARGERS, lcfs = LCFS, eer = EER_VALUES, gridIntensity = GRID_INTENSITY, liabilityRates = LIABILITY_RATES, comprehensiveRates = COMPREHENSIVE_RATES, }) => {
    const [state, dispatch] = useReducer(calculationsReducer, {});
    const [status, setStatus] = useState(INITIAL_RESULTS_STATE.status);
    const [error, setError] = useState(false);
    const [inputs, setInputs] = useState();
    useEffect(() => {
        (async () => {
            try {
                const path = dynamicModulePath.split('\\').join('/');
                const calcs = await requireDynamically(path);
                calcs.setData({
                    chargers,
                    lcfs,
                    eer,
                    liabilityRates,
                    comprehensiveRates,
                    gridIntensity,
                });
                dispatch({ type: 'calculations', payload: calcs });
            }
            catch (error) {
                setError(Boolean(error));
            }
            finally {
                setStatus({
                    ...status,
                    calculations: { isReady: true },
                });
            }
        })();
    }, []);
    return (_jsx(CalculationsContext.Provider, { value: { state, dispatch, status, setStatus, inputs, setInputs, error }, children: children }));
};
export const useCalculationsStatus = () => {
    const { status, error } = useContext(CalculationsContext);
    return { isReady: status.calculations.isReady, error };
};
export const useCalculations = () => {
    const { state } = useContext(CalculationsContext);
    return state.calculations || {};
};
export const useFinanceCalculations = () => {
    const { state } = useContext(CalculationsContext);
    return state?.calculations?.financeCalculations || {};
};
export const useDynamicCalculations = (...calculationNames) => {
    const { state } = useContext(CalculationsContext);
    return calculationNames.map((calc) => state?.calculations?.[calc]);
};
export const useCalculationsInput = () => {
    const { dispatch, setStatus, setInputs } = useContext(CalculationsContext);
    return (input) => {
        let newInput = undefined;
        if (input) {
            newInput = {
                ...input,
                uid: Math.random().toString(),
            };
        }
        setStatus(() => INITIAL_RESULTS_STATE.status);
        setInputs(() => newInput);
        dispatch([
            {
                type: 'inputs',
                payload: newInput,
            },
            { type: 'initial', payload: INITIAL_RESULTS_STATE },
        ]);
    };
};
export const useCalculationSelector = () => {
    const { state, dispatch, status, setStatus, inputs } = useContext(CalculationsContext);
    return stateAccess(state, dispatch, status, setStatus, inputs);
};
